import React, { useState, useEffect } from "react";
import styled from "styled-components";
import axios from "axios";
import { Link } from "react-router-dom";
import { ChevronLeft, Calendar, History, TrendingUp, TrendingDown, ExternalLink } from "lucide-react";

const Page = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 64px);
  background: #141414;
  color: #fff;
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`;

const BackLink = styled(Link)`
  color: #aaa;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  &:hover {
    color: #fff;
  }
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
`;

const Content = styled.div`
  display: flex;
  gap: 24px;
  flex: 1;
  overflow: hidden;
`;

const Sidebar = styled.div`
  width: 280px;
  background: #1f1f1f;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SidebarHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #333;
  font-size: 14px;
  font-weight: 600;
  color: #aaa;
`;

const SidebarList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;
`;

const SidebarItem = styled.div`
  padding: 12px;
  border-radius: 6px;
  cursor: pointer;
  margin-bottom: 4px;
  background: ${props => props.$active ? '#333' : 'transparent'};
  border: 1px solid ${props => props.$active ? '#444' : 'transparent'};
  &:hover {
    background: ${props => props.$active ? '#333' : '#252525'};
  }
`;

const DateText = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #eee;
  margin-bottom: 4px;
`;

const StrategyName = styled.div`
  font-size: 11px;
  color: #888;
`;

const MainArea = styled.div`
  flex: 1;
  background: #1f1f1f;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ListHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #333;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ResultList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px;
  color: #666;
  font-size: 12px;
  font-weight: 500;
  border-bottom: 1px solid #333;
`;

const Td = styled.td`
  padding: 16px 12px;
  border-bottom: 1px solid #282828;
  vertical-align: middle;
`;

const SymbolBox = styled.div`
  display: flex;
  flex-direction: column;
`;

const SymbolName = styled.span`
  font-weight: 600;
  color: #fff;
`;

const SymbolCode = styled.span`
  font-size: 11px;
  color: #666;
`;

const PriceValue = styled.div`
  font-size: 14px;
  font-family: 'Roboto Mono', monospace;
`;

const ReturnBadge = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  background: ${props => props.$positive ? 'rgba(229, 9, 20, 0.15)' : 'rgba(0, 113, 227, 0.15)'};
  color: ${props => props.$positive ? '#e50914' : '#0071e7'};
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const LoadingOverlay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #666;
`;

const getBaseUrl = () => {
    return (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
        ? 'http://localhost:5000'
        : window.location.origin;
};

const ScreenerHistoryPage = () => {
    const [dates, setDates] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const [historyData, setHistoryData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [detailsLoading, setDetailsLoading] = useState(false);

    useEffect(() => {
        fetchDates();
    }, []);

    const fetchDates = async () => {
        try {
            const res = await axios.get(`${getBaseUrl()}/api/batch/screener/history`);
            setDates(res.data);
            if (res.data.length > 0) {
                handleDateSelect(res.data[0].id);
            }
            setLoading(false);
        } catch (e) {
            console.error('Failed to fetch history dates:', e);
            setLoading(false);
        }
    };

    const handleDateSelect = async (date) => {
        setSelectedDate(date);
        setDetailsLoading(true);
        try {
            const res = await axios.get(`${getBaseUrl()}/api/batch/screener/history/${date}`);
            setHistoryData(res.data);
        } catch (e) {
            console.error('Failed to fetch history details:', e);
        } finally {
            setDetailsLoading(false);
        }
    };

    if (loading) {
        return (
            <Page>
                <LoadingOverlay>
                    <History size={40} />
                    <span style={{ marginTop: '12px' }}>히스토리 로딩 중...</span>
                </LoadingOverlay>
            </Page>
        );
    }

    return (
        <Page>
            <Header>
                <BackLink to="/labs/screener">
                    <ChevronLeft size={16} /> 스크리너로 돌아가기
                </BackLink>
                <Title><History size={20} color="#e50914" /> 스크리너 히스토리 (Top 10)</Title>
            </Header>

            <Content>
                <Sidebar>
                    <SidebarHeader>저장된 날짜</SidebarHeader>
                    <SidebarList>
                        {dates.length === 0 ? (
                            <div style={{ padding: '20px', color: '#555', fontSize: '12px' }}>기록이 없습니다.</div>
                        ) : (
                            dates.map(d => (
                                <SidebarItem 
                                    key={d.id} 
                                    $active={selectedDate === d.id}
                                    onClick={() => handleDateSelect(d.id)}
                                >
                                    <DateText><Calendar size={12} style={{ marginRight: '4px' }} /> {d.date}</DateText>
                                    <StrategyName>{d.strategyName}</StrategyName>
                                    <div style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}>
                                        run: {d.id.split('_')[1] ? `${d.id.split('_')[1].substring(0,2)}:${d.id.split('_')[1].substring(2,4)}:${d.id.split('_')[1].substring(4,6)}` : ''}
                                    </div>
                                </SidebarItem>
                            ))
                        )}
                    </SidebarList>
                </Sidebar>

                <MainArea>
                    {detailsLoading ? (
                        <LoadingOverlay>
                            <span className="loader">상세 정보 조회 중...</span>
                        </LoadingOverlay>
                    ) : historyData ? (
                        <>
                            <ListHeader>
                                <div>
                                    <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '4px' }}>{historyData.date} 분석 결과</div>
                                    <div style={{ fontSize: '13px', color: '#888' }}>전략: {historyData.strategyName}</div>
                                </div>
                            </ListHeader>
                            <ResultList>
                                <Table>
                                    <thead>
                                        <tr>
                                            <Th>종목</Th>
                                            <Th>신호가 (일자)</Th>
                                            <Th>현재가</Th>
                                            <Th>전략 성과 (당시)</Th>
                                            <Th>신호 이후 수익률</Th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {historyData.hits.map(hit => {
                                            // Calculate return from the signal price (historical) to the current live price
                                            const perf = hit.currentPrice 
                                                ? ((hit.currentPrice - hit.signalPrice) / hit.signalPrice * 100).toFixed(2)
                                                : null;
                                            const isPositive = parseFloat(perf) >= 0;

                                            // Format signal date
                                            const formattedSignalDate = String(hit.signalDate || '').replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3');

                                            return (
                                                <tr key={hit.symbol}>
                                                    <Td>
                                                        <SymbolBox>
                                                            <SymbolName>{hit.name}</SymbolName>
                                                            <SymbolCode>{hit.symbol}</SymbolCode>
                                                        </SymbolBox>
                                                    </Td>
                                                    <Td>
                                                        <PriceValue>{hit.signalPrice.toLocaleString()}원</PriceValue>
                                                        <div style={{ fontSize: '11px', color: '#555' }}>{formattedSignalDate}</div>
                                                    </Td>
                                                    <Td>
                                                        <PriceValue>{hit.currentPrice ? `${hit.currentPrice.toLocaleString()}원` : 'N/A'}</PriceValue>
                                                    </Td>
                                                    <Td>
                                                        <div style={{ fontSize: '13px' }}>
                                                            수익: <span style={{ color: parseFloat(hit.totalReturn) >= 0 ? '#ff4b4b' : '#4b89ff' }}>{hit.totalReturn}%</span>
                                                        </div>
                                                        <div style={{ fontSize: '11px', color: '#555' }}>승률: {hit.winRate}%</div>
                                                    </Td>
                                                    <Td>
                                                        {perf !== null ? (
                                                            <ReturnBadge $positive={isPositive}>
                                                                {isPositive ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                                                                {isPositive ? '+' : ''}{perf}%
                                                            </ReturnBadge>
                                                        ) : (
                                                            <span style={{ color: '#444' }}>-</span>
                                                        )}
                                                    </Td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </ResultList>
                        </>
                    ) : (
                        <LoadingOverlay>
                            <span>날짜를 선택하여 히스토리를 확인하세요.</span>
                        </LoadingOverlay>
                    )}
                </MainArea>
            </Content>
        </Page>
    );
};

export default ScreenerHistoryPage;
